import { useCallback, useMemo, useState } from 'react';
import { buildMissingMetricsErrorMessage, SignalsModuleShell } from '../core';
import type { AssetLeaderboardSortPreset } from './AssetLeaderboard.types';
import { AssetLeaderboardLoadingBar } from './AssetLeaderboardLoadingBar';
import { AssetLeaderboardToolbar } from './AssetLeaderboardToolbar';
import { AssetLeaderboardView } from './AssetLeaderboardView';
import { ASSET_LEADERBOARD_DEFAULT_SORT_PRESET, ASSET_LEADERBOARD_METRIC_LABELS } from './tokens';
import { useAssetLeaderboardData } from './useAssetLeaderboardData';

interface AssetLeaderboardModuleProps {
  blotterID: string;
}

/**
 * Top-level module: fetches the full asset universe and renders the sort-preset toolbar + grid.
 * No settings/config view in this slice — nothing is user-configurable yet (full universe, fixed
 * EOD metrics); that lands with the asset-picker / time-window / preset-management follow-on work.
 */
export function AssetLeaderboardModule({ blotterID }: AssetLeaderboardModuleProps) {
  // null = no preset is active (the user is sorting by a column directly), so no toolbar button is
  // highlighted. Starts at the default preset because that's also the grid's default sort.
  const [sortPreset, setSortPreset] = useState<AssetLeaderboardSortPreset | null>(
    ASSET_LEADERBOARD_DEFAULT_SORT_PRESET
  );
  // Distinguishes "sortPreset is still at its initial value because the user hasn't touched the
  // toolbar yet" from "the user explicitly clicked a preset" — the grid should only ever force-apply
  // a preset for the latter (see AssetLeaderboardView's effect for why).
  const [hasUserChangedSortPreset, setHasUserChangedSortPreset] = useState(false);
  const handleSortPresetChange = useCallback((preset: AssetLeaderboardSortPreset) => {
    setHasUserChangedSortPreset(true);
    setSortPreset(preset);
  }, []);
  // Fired by the grid when the active sort stops matching the selected preset — i.e. the user sorted a
  // column directly. Clearing the preset deselects its toolbar button and, because the next click on
  // that same preset is then a real state change (null -> preset), lets it re-apply its sort.
  const handleSortClearedByColumn = useCallback(() => {
    setSortPreset(null);
  }, []);

  const { rows, assetCount, loadedAssetCount, isLoading, isError, missingMetrics } = useAssetLeaderboardData();
  const hasMissingMetrics = missingMetrics.length > 0;
  const showGrid = !(isLoading && rows.length === 0) && !isError && !hasMissingMetrics;

  // A missing-metrics key is a permission problem, not a "no data" one — name what's inaccessible
  // rather than showing an empty/partial grid the user has no way to explain on their own.
  const errorMessage = useMemo(() => {
    if (!hasMissingMetrics) {
      return 'Failed to load asset data.';
    }
    return buildMissingMetricsErrorMessage(
      missingMetrics.map(metric => ASSET_LEADERBOARD_METRIC_LABELS[metric] ?? metric)
    );
  }, [hasMissingMetrics, missingMetrics]);

  return (
    <SignalsModuleShell
      header={
        showGrid && (
          <AssetLeaderboardToolbar activeSortPreset={sortPreset} onSortPresetChange={handleSortPresetChange} />
        )
      }
      isLoading={isLoading && rows.length === 0}
      loadingContent={<AssetLeaderboardLoadingBar assetCount={assetCount} loadedAssetCount={loadedAssetCount} />}
      isError={isError}
      errorMessage={errorMessage}
      isPermissionError={hasMissingMetrics}
    >
      <AssetLeaderboardView
        rows={rows}
        blotterID={blotterID}
        sortPreset={sortPreset}
        hasUserChangedSortPreset={hasUserChangedSortPreset}
        onSortClearedByColumn={handleSortClearedByColumn}
      />
    </SignalsModuleShell>
  );
}
